/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { ButtonProps, IconProps } from '@coursera/cds-core';
import { Typography2, breakpoints } from '@coursera/cds-core';
import type { ButtonName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import type { ModuleLayoutConfig } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import ModuleLayout, { MODULE_LAYOUTS, isHorizontalLayout } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import type { ImageType } from 'bundles/cds-labs/components/Modules/components/Image';
import Image from 'bundles/cds-labs/components/Modules/components/Image';
import PreservedUtmButton from 'bundles/cds-labs/components/Modules/components/PreservedUtmButton';
import { getFgColorFromBgColorName, isInvertedColor } from 'bundles/cds-labs/components/Modules/utils/colors';

type Icon = React.ReactElement<IconProps>;

export type ValuePropItem = {
  id: string;
  description?: React.ReactNode | null;
  headline?: string | null;
  icon?: ImageType | Icon | null;
  ctaText?: string | null;
  ctaTarget?: string | null;
  ctaVariant?: string | null;
};

type Props = {
  description?: React.ReactNode | null;
  headline?: string | null;
  valueProps?: ValuePropItem[];
  cardBackgroundColor?: string | null;
  cardClassName?: string;
} & ModuleLayoutConfig;

const styles = {
  moduleDescription: (isDarkBackground: boolean) => css`
    p {
      margin-top: var(--cds-spacing-50) !important;
      margin-bottom: 0 !important;
      color: ${
        isDarkBackground ? 'var(--cds-color-neutral-background-primary-weak)' : 'var(--cds-color-neutral-primary-weak)'
      } !important;
    }
  `,
  cardDescription: css`
    p {
      margin-bottom: 0 !important;
      margin-top: 2px !important;
    }
  `,
  cards: css`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    width: 100%;
    gap: var(--cds-spacing-200);

    ${breakpoints.down('xs')} {
      gap: var(--cds-spacing-100);
    }
  `,

  headingVerticalLayout: (isCentered: boolean) => css`
    margin-bottom: var(--cds-spacing-150);
    ${isCentered ? 'text-align: center; justify-self: center;' : ''}
    ${breakpoints.up('sm')} {
      ${isCentered ? 'max-width: 50%;' : ''}
    }
  `,

  noTopPadding: css`
    && {
      padding-top: 0;
    }
  `,

  noBottomPadding: css`
    && {
      padding-bottom: 0;
    }
  `,

  card: (cardBasis: string, cardBackgroundColor: string, cardTextColor: string, isCentered: boolean) => css`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: ${isCentered ? 'center' : 'start'};
    ${isCentered ? 'text-align: center;' : ''}
    padding: var(--cds-spacing-200) var(--cds-spacing-300);
    background-color: ${cardBackgroundColor};
    color: ${cardTextColor};
    border-radius: var(--cds-border-radius-100);
    gap: var(--cds-spacing-100);
    flex-basis: calc(${cardBasis} - var(--cds-spacing-400));

    ${breakpoints.down('xs')} {
      flex-basis: 100%;
      flex-direction: row;
      align-items: flex-start;
      text-align: left;
      padding: var(--cds-spacing-100) var(--cds-spacing-300);
    }
  `,

  cardContent: css`
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
  `,

  cardHorizontalLayout: css`
    align-items: flex-start;
    text-align: left;
  `,

  cardTwoColumns: css`
    flex-basis: calc(50% - var(--cds-spacing-400));
  `,

  ctaSection: css`
    margin-top: auto;
    padding-top: var(--cds-spacing-200);

    ${breakpoints.down('xs')} {
      .cds-button-secondary,
      .cds-button-ghost {
        width: 100%;
        max-width: 100%;
      }
    }
  `,
};

export function ValuePropsModule(props: Props) {
  const isHorizontal = isHorizontalLayout(props);
  const track = useTracker();

  const trackClick = (trackingName: ButtonName, linkTarget?: string) => () => {
    track('click_merchandising_module', {
      button: { name: trackingName, linkURL: linkTarget ?? '' },
      merchandisingModule: {
        moduleType: 'value_props_module',
      },
    });
  };

  const getCustomContentPanelStyles = () => {
    if (props.layout === MODULE_LAYOUTS.textTopImageBottom) {
      return [styles.noBottomPadding];
    }
    if (props.layout === MODULE_LAYOUTS.imageTopTextBottom) {
      return [styles.noTopPadding];
    }
    return undefined;
  };

  const getCustomImagePanelStyles = () => {
    if (props.layout === MODULE_LAYOUTS.textTopImageBottom) {
      return [styles.noTopPadding];
    }
    if (props.layout === MODULE_LAYOUTS.imageTopTextBottom) {
      return [styles.noBottomPadding];
    }
    return undefined;
  };

  // dynamically decide the card width
  const numCards = props.valueProps?.length || 0;
  let cardBasis = '50%';
  if (!isHorizontal && numCards > 4) {
    cardBasis = '33.3%';
  } else if (!isHorizontal) {
    cardBasis = `${100 / numCards}%`;
  }

  const isCentered = props.layout === MODULE_LAYOUTS.textTopAndCentered;

  const isDarkBackground = props.backgroundColor ? isInvertedColor(props.backgroundColor) : false;

  const cardBackgroundColor = props.cardBackgroundColor ?? 'var(--cds-color-neutral-background-primary)';
  let isCardBackgroundDark = isInvertedColor(cardBackgroundColor);
  let cardTextColor = 'var(--cds-color-neutral-primary)';

  if (cardBackgroundColor === 'transparent') {
    if (props.backgroundColor) {
      cardTextColor = getFgColorFromBgColorName(props.backgroundColor);
      isCardBackgroundDark = isInvertedColor(props.backgroundColor);
    }
  } else {
    cardTextColor = getFgColorFromBgColorName(cardBackgroundColor);
  }

  const cardPanel = () => (
    <div css={styles.cards}>
      {props.valueProps?.map((card) => {
        if (!card) return null;

        let icon: React.ReactNode = null;
        if (card.icon && 'url' in card.icon) {
          // the icon is an image object
          icon = <Image {...card.icon} />;
        } else if (card.icon) {
          // the icon is a CDS icon
          icon = card.icon as Icon;
        }

        // Adjust button variant based on card background color for better contrast
        let buttonVariant = card.ctaVariant as ButtonProps['variant'];
        if (card.ctaVariant === 'primary') {
          buttonVariant = isCardBackgroundDark ? 'primaryInvert' : 'primary';
        } else if (card.ctaVariant === 'ghost') {
          buttonVariant = isCardBackgroundDark ? 'ghostInvert' : 'ghost';
        }

        return (
          <div
            css={[
              styles.card(cardBasis, cardBackgroundColor, cardTextColor, isCentered),
              isHorizontal ? styles.cardHorizontalLayout : null,
            ]}
            className={props.cardClassName}
            key={card.id}
          >
            {icon}
            <div css={styles.cardContent}>
              <Typography2 variant="subtitleMedium" component="h3" color="inherit">
                {card?.headline}
              </Typography2>
              <Typography2 variant="bodySecondary" component="div" color="inherit" css={styles.cardDescription}>
                {card?.description}
              </Typography2>
              {card?.ctaText && card?.ctaTarget && card?.ctaVariant && (
                <div css={styles.ctaSection}>
                  <PreservedUtmButton
                    variant={buttonVariant}
                    href={card.ctaTarget}
                    trackingName="primary_cta"
                    onClick={trackClick('primary_cta', card.ctaTarget)}
                  >
                    {card.ctaText}
                  </PreservedUtmButton>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  const contentPanel = () => {
    // if title and description are empty, don't render anything to prevent extra spacing
    if (!props.description && !props.headline) {
      return null;
    }
    return (
      <div css={[isHorizontal ? null : styles.headingVerticalLayout(isCentered)]}>
        {props.headline && (
          <Typography2 variant="titleXSmall" component="h2" color="inherit">
            {props.headline}
          </Typography2>
        )}

        {props.description && (
          <Typography2
            variant="bodySecondary"
            component="div"
            color="inherit"
            css={styles.moduleDescription(isDarkBackground)}
          >
            {props.description}
          </Typography2>
        )}
      </div>
    );
  };

  const getAssemblyProps = (assemblyProps: Props) => {
    // return props as-is if the layout is horizontal or there is no content to render
    if (isHorizontal || (!props.description && !props.headline)) return assemblyProps;

    return {
      ...assemblyProps,
      customImagePanelStyles: getCustomImagePanelStyles(),
      customContentPanelStyles: getCustomContentPanelStyles(),
    };
  };

  return <ModuleLayout assembly={getAssemblyProps(props)} renderContent={contentPanel} renderImage={cardPanel} />;
}

export default ValuePropsModule;
